import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  FileText,
  Upload,
  Download,
  Loader2,
  Plus,
  X,
  FileCheck,
  Building,
  User,
  Calendar,
  Hash,
  FolderOpen,
  Award,
  BookOpen,
  Pencil,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

export default function SubmittalGenerator() {
  // Form state
  const [projectName, setProjectName] = useState('');
  const [submittalNumber, setSubmittalNumber] = useState('');
  const [submittalTitle, setSubmittalTitle] = useState('');
  const [contractor, setContractor] = useState('Distinct High Road (DHR)');
  const [consultant, setConsultant] = useState('');
  const [client, setClient] = useState('');
  
  // Files state
  const [datasheets, setDatasheets] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [manuals, setManuals] = useState([]);
  
  // UI state
  const [generating, setGenerating] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [result, setResult] = useState(null);
  
  // File input refs
  const datasheetRef = useRef(null);
  const certificateRef = useRef(null);
  const drawingRef = useRef(null);
  const manualRef = useRef(null);

  // Generate submittal number
  const generateSubmittalNumber = () => {
    const date = new Date();
    const num = `SUB-${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    setSubmittalNumber(num);
  };

  // Handle file upload
  const handleFileUpload = async (files, setFiles, category) => {
    const fileArray = Array.from(files);
    const uploadedPaths = [];
    
    for (const file of fileArray) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${file.name}: يجب أن يكون الملف PDF`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.file_path) {
          uploadedPaths.push({
            name: file.name,
            path: response.data.file_path,
            size: file.size
          });
        }
      } catch (e) {
        toast.error(`فشل رفع ${file.name}`);
      }
    }
    
    if (uploadedPaths.length > 0) {
      setFiles(prev => [...prev, ...uploadedPaths]);
      toast.success(`تم رفع ${uploadedPaths.length} ملف`);
    }
  };

  // Remove file
  const removeFile = (index, setFiles) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Generate cover page only
  const handleGenerateCover = async () => {
    if (!projectName || !submittalTitle) {
      toast.error('يرجى إدخال اسم المشروع وعنوان الـ Submittal');
      return;
    }
    
    setGeneratingCover(true);
    try {
      const params = new URLSearchParams({
        project_name: projectName,
        submittal_number: submittalNumber || `SUB-${Date.now()}`,
        submittal_title: submittalTitle,
        contractor: contractor || 'DHR',
        consultant: consultant || 'Consultant',
        client: client || 'Client'
      });
      
      const response = await api.post(`/submittals/generate-cover?${params.toString()}`);
      
      if (response.data.success) {
        toast.success('تم إنشاء صفحة الغلاف بنجاح');
        setResult({
          type: 'cover',
          ...response.data
        });
      } else {
        toast.error(response.data.error || 'فشل إنشاء الغلاف');
      }
    } catch (e) {
      toast.error('حدث خطأ أثناء إنشاء الغلاف');
    } finally {
      setGeneratingCover(false);
    }
  };

  // Generate full submittal
  const handleGenerateSubmittal = async () => {
    if (!projectName || !submittalTitle) {
      toast.error('يرجى إدخال اسم المشروع وعنوان الـ Submittal');
      return;
    }
    
    if (datasheets.length === 0 && certificates.length === 0 && drawings.length === 0 && manuals.length === 0) {
      toast.error('يرجى رفع ملف واحد على الأقل');
      return;
    }
    
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        project_name: projectName,
        submittal_title: submittalTitle,
        contractor: contractor || 'DHR',
        consultant: consultant || 'Consultant',
        client: client || 'Client'
      });
      
      // Add file paths
      datasheets.forEach(f => params.append('datasheet_files', f.path));
      certificates.forEach(f => params.append('certificate_files', f.path));
      drawings.forEach(f => params.append('drawing_files', f.path));
      manuals.forEach(f => params.append('manual_files', f.path));
      
      const response = await api.post(`/submittals/create?${params.toString()}`);
      
      if (response.data.success) {
        toast.success('تم إنشاء الـ Submittal بنجاح');
        setResult({
          type: 'full',
          ...response.data
        });
      } else {
        toast.error(response.data.error || 'فشل إنشاء الـ Submittal');
      }
    } catch (e) {
      toast.error('حدث خطأ أثناء إنشاء الـ Submittal');
    } finally {
      setGenerating(false);
    }
  };

  // Download result
  const handleDownload = async () => {
    if (!result?.file_path) return;
    
    try {
      const response = await api.get('/files/download', {
        params: { path: result.file_path },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', result.file_name || 'submittal.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('تم تحميل الملف');
    } catch (e) {
      toast.error('فشل تحميل الملف');
    }
  };

  // Reset form
  const handleReset = () => {
    setProjectName('');
    setSubmittalNumber('');
    setSubmittalTitle('');
    setConsultant('');
    setClient('');
    setDatasheets([]);
    setCertificates([]);
    setDrawings([]);
    setManuals([]);
    setResult(null);
  };

  // File section component
  const FileSection = ({ title, titleEn, icon: Icon, files, setFiles, inputRef, color }) => (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Icon className={`h-5 w-5 ${color}`} />
            {title}
            <span className="text-xs text-muted-foreground font-normal">({titleEn})</span>
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {files.length} ملف
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <input
          type="file"
          ref={inputRef}
          className="hidden"
          accept=".pdf"
          multiple
          onChange={(e) => handleFileUpload(e.target.files, setFiles, title)}
        />
        
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => inputRef.current?.click()}
          data-testid={`upload-${titleEn.toLowerCase().replace(' ', '-')}`}
        >
          <Upload className="h-4 w-4 mr-2" />
          رفع ملفات PDF
        </Button>
        
        {files.length > 0 && (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 rounded bg-muted/30 border border-border text-sm"
              >
                <div className="flex items-center gap-2 truncate">
                  <FileCheck className={`h-4 w-4 ${color} flex-shrink-0`} />
                  <span className="truncate">{file.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      if (file.path) {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/download?path=${encodeURIComponent(file.path)}`, '_blank');
                      }
                    }}
                    data-testid={`view-attached-${index}`}
                  >
                    View Attached
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                    onClick={() => removeFile(index, setFiles)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
              مولد الـ Submittal
            </h1>
            <p className="text-muted-foreground mt-1">Submittal Generator</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
          <Plus className="h-4 w-4 mr-2" />
          جديد
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Project Info */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-primary" />
                معلومات المشروع
              </CardTitle>
              <CardDescription>Project Information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  اسم المشروع *
                </Label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="مثال: مشروع برج الرياض"
                  data-testid="project-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  رقم الـ Submittal
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={submittalNumber}
                    onChange={(e) => setSubmittalNumber(e.target.value)}
                    placeholder="SUB-20260204-001"
                    data-testid="submittal-number-input"
                  />
                  <Button variant="outline" size="icon" onClick={generateSubmittalNumber} title="توليد تلقائي">
                    <Hash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  عنوان الـ Submittal *
                </Label>
                <Input
                  value={submittalTitle}
                  onChange={(e) => setSubmittalTitle(e.target.value)}
                  placeholder="مثال: Fire Dampers - HVAC System"
                  data-testid="submittal-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  المقاول (Contractor)
                </Label>
                <Input
                  value={contractor}
                  onChange={(e) => setContractor(e.target.value)}
                  placeholder="Distinct High Road"
                  data-testid="contractor-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  الاستشاري (Consultant)
                </Label>
                <Input
                  value={consultant}
                  onChange={(e) => setConsultant(e.target.value)}
                  placeholder="اسم الاستشاري"
                  data-testid="consultant-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  العميل (Client)
                </Label>
                <Input
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  placeholder="اسم العميل"
                  data-testid="client-input"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base">إجراءات سريعة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full"
                variant="outline"
                onClick={handleGenerateCover}
                disabled={generatingCover || !projectName || !submittalTitle}
                data-testid="generate-cover-btn"
              >
                {generatingCover ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                إنشاء صفحة الغلاف فقط
              </Button>
              
              <Button
                className="w-full"
                onClick={handleGenerateSubmittal}
                disabled={generating || !projectName || !submittalTitle}
                data-testid="generate-submittal-btn"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileCheck className="h-4 w-4 mr-2" />
                )}
                إنشاء Submittal كامل
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Files */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileSection
              title="بيانات المنتج"
              titleEn="Product Datasheets"
              icon={FileText}
              files={datasheets}
              setFiles={setDatasheets}
              inputRef={datasheetRef}
              color="text-blue-500"
            />
            
            <FileSection
              title="الشهادات"
              titleEn="Certificates"
              icon={Award}
              files={certificates}
              setFiles={setCertificates}
              inputRef={certificateRef}
              color="text-green-500"
            />
            
            <FileSection
              title="الرسومات"
              titleEn="Drawings"
              icon={Pencil}
              files={drawings}
              setFiles={setDrawings}
              inputRef={drawingRef}
              color="text-orange-500"
            />
            
            <FileSection
              title="أدلة التشغيل"
              titleEn="Manuals"
              icon={BookOpen}
              files={manuals}
              setFiles={setManuals}
              inputRef={manualRef}
              color="text-purple-500"
            />
          </div>
          
          {/* Summary */}
          <Card className="bg-muted/30 border-border">
            <CardContent className="py-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">إجمالي الملفات:</span>
                <Badge variant="outline">
                  {datasheets.length + certificates.length + drawings.length + manuals.length} ملف
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          {/* Result */}
          {result && (
            <Card className="bg-green-500/10 border-green-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-500">
                  <CheckCircle className="h-5 w-5" />
                  تم إنشاء الـ {result.type === 'cover' ? 'غلاف' : 'Submittal'} بنجاح!
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">اسم الملف:</span>
                    <p className="font-mono">{result.file_name}</p>
                  </div>
                  {result.sections_count && (
                    <div>
                      <span className="text-muted-foreground">عدد الأقسام:</span>
                      <p>{result.sections_count}</p>
                    </div>
                  )}
                </div>
                
                <Button onClick={handleDownload} className="w-full" data-testid="download-btn">
                  <Download className="h-4 w-4 mr-2" />
                  تحميل الملف
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Instructions */}
      <Card className="bg-muted/30 border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            تعليمات الاستخدام
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div>
              <h4 className="font-medium text-foreground mb-2">هيكل الـ Submittal:</h4>
              <ol className="list-decimal list-inside space-y-1">
                <li>صفحة الغلاف (Cover Page)</li>
                <li>جدول المحتويات (Table of Contents)</li>
                <li>بيانات المنتج (Product Datasheets)</li>
                <li>الشهادات والاعتمادات (Certificates)</li>
                <li>رسومات التركيب (Drawings)</li>
                <li>أدلة التشغيل والصيانة (Manuals)</li>
              </ol>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">ملاحظات:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>جميع الملفات يجب أن تكون بصيغة PDF</li>
                <li>سيتم دمج الملفات تلقائياً مع فواصل الأقسام</li>
                <li>يمكنك إنشاء صفحة الغلاف فقط بدون ملفات</li>
                <li>رقم الـ Submittal يُولد تلقائياً إذا لم تدخله</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
